import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MemberManagementViewModel } from '../appService';
import { MEMBER_MANAGEMENT_DATA_RESET_REQUIRED_MESSAGE } from '../model';
import type { WorkspaceTab } from '../workspaceRoutes';
import type {
  AppBootstrapContext,
  AppRuntime,
  BootstrapContextValue,
  ContextResetController,
  RouteRestorationController,
  RouteRestorationSnapshot,
  WorkspaceContextValue
} from './appTypes';
import {
  BootstrapContext,
  ContextResetControllerContext,
  RouteRestorationControllerContext,
  WorkspaceContext,
  useAppRuntime,
  useWorkspaceRouteAdapter
} from './context';
import { resetContextWorkspaceState, useBootstrapQuery } from './data';
import { isWorkspaceTab, WorkspaceRouteAdapterProvider } from './navigation';
import { Icon, type IconName } from './ui';

type BootstrapRouteResetTarget = 'auth' | 'onboarding' | 'workspace';

type AppShellProps = {
  readonly authRoute: ReactNode;
  readonly onboardingRoute: ReactNode;
};

export function AppShell({ authRoute, onboardingRoute }: AppShellProps): ReactNode {
  const runtime = useAppRuntime();
  const bootstrap = useBootstrapQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeRestorationKey = useRouterState({ select: (state) => state.location.href });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const lastBootstrapContextKey = useRef<string | null>(null);
  const lastWorkspaceTabRef = useRef<WorkspaceTab | null>(null);
  const lastRouteRestorationKeyRef = useRef(routeRestorationKey);
  const lastPathnameRef = useRef(pathname);
  const lastDrillDownResetPathRef = useRef<string | null>(null);
  const screenRef = useRef<HTMLElement | null>(null);
  const routeRestorationSnapshotsRef = useRef(new Map<string, RouteRestorationSnapshot>());
  const pendingHistoryBackRef = useRef(false);
  const pendingContextResetTargetPath = useRef<string | null>(null);
  const preparedContextResetRef = useRef(false);
  const [contextResetVersion, setContextResetVersion] = useState(0);
  const workspaceContext = useMemo(() => getWorkspaceContext(bootstrap.data), [bootstrap.data]);
  const bootstrapContext = useMemo<BootstrapContextValue>(
    () => ({
      status: bootstrap.status,
      appContext: bootstrap.data ? getAppBootstrapContext(bootstrap.data) : null,
      error: bootstrap.error ?? null,
      retry: () => {
        void bootstrap.refetch();
      }
    }),
    [bootstrap.data, bootstrap.error, bootstrap.refetch, bootstrap.status]
  );
  const workspaceTab = getWorkspaceTabForShell(bootstrap.data, pathname);
  const screenName = getScreenName(bootstrap.data, bootstrap.status, pathname, workspaceTab);
  const shouldShowBottomBar = Boolean(workspaceContext && workspaceTab);
  const beginContextMutationReset = useCallback(() => {
    preparedContextResetRef.current = true;
    routeRestorationSnapshotsRef.current.clear();
    resetContextWorkspaceState(queryClient);
    const screen = screenRef.current;
    if (screen) {
      resetScreenAfterContextChange(screen);
    }
  }, [queryClient]);
  const routeRestorationController = useMemo<RouteRestorationController>(
    () => ({
      capturePushRestoration: () => {
        const screen = screenRef.current;
        if (!screen) {
          return;
        }
        captureRouteRestorationSnapshot(routeRestorationSnapshotsRef.current, routeRestorationKey, screen);
      },
      clearRestorationSnapshots: () => {
        routeRestorationSnapshotsRef.current.clear();
      }
    }),
    [routeRestorationKey]
  );
  const contextResetController = useMemo<ContextResetController>(
    () => ({
      beginContextMutationReset
    }),
    [beginContextMutationReset]
  );
  useEffect(
    () =>
      runtime.routerHistory.subscribe(({ action }) => {
        if (action.type === 'REPLACE') {
          pendingHistoryBackRef.current = false;
          routeRestorationSnapshotsRef.current.delete(lastRouteRestorationKeyRef.current);
          return;
        }

        if (action.type === 'PUSH') {
          const screen = screenRef.current;
          if (screen) {
            captureRouteRestorationSnapshot(
              routeRestorationSnapshotsRef.current,
              lastRouteRestorationKeyRef.current,
              screen
            );
          }
          pendingHistoryBackRef.current = false;
          return;
        }

        pendingHistoryBackRef.current = action.type === 'BACK' || (action.type === 'GO' && action.index < 0);
      }),
    [runtime.routerHistory]
  );
  useEffect(() => {
    if (bootstrap.status !== 'success' || !bootstrap.data) {
      return;
    }

    const nextContextKey = getBootstrapContextKey(bootstrap.data);
    const previousContextKey = lastBootstrapContextKey.current;
    lastBootstrapContextKey.current = nextContextKey;

    const target = getBootstrapRouteResetTarget(bootstrap.data);
    const shouldReset =
      previousContextKey === null
        ? !isRouteCompatibleWithBootstrapTarget(pathname, target)
        : previousContextKey !== nextContextKey;

    if (!shouldReset) {
      return;
    }

    const targetPath = getContextResetTargetPath(target);
    const wasPreparedContextReset = preparedContextResetRef.current;
    preparedContextResetRef.current = false;
    routeRestorationSnapshotsRef.current.clear();
    if (!wasPreparedContextReset) {
      resetContextWorkspaceState(queryClient);
    }
    pendingContextResetTargetPath.current = targetPath;
    setContextResetVersion((version) => version + 1);
    if (target === 'workspace') {
      void navigate({
        to: '/workspace/$tab',
        params: { tab: 'home' },
        replace: true,
        resetScroll: true
      });
      return;
    }

    void navigate({
      to: target === 'auth' ? '/auth' : '/onboarding',
      replace: true,
      resetScroll: true
    });
  }, [bootstrap.data, bootstrap.status, navigate, pathname, queryClient]);

  useEffect(() => {
    if (pendingContextResetTargetPath.current !== pathname) {
      return;
    }

    const screen = screenRef.current;
    if (!screen) {
      return;
    }

    resetScreenAfterContextChange(screen);
    pendingContextResetTargetPath.current = null;
  }, [contextResetVersion, pathname, screenName]);

  useEffect(() => {
    const previousTab = lastWorkspaceTabRef.current;
    lastWorkspaceTabRef.current = workspaceTab;
    if (
      bootstrap.status !== 'success' ||
      !workspaceContext ||
      !workspaceTab ||
      !previousTab ||
      previousTab === workspaceTab
    ) {
      return;
    }

    const screen = screenRef.current;
    if (screen) {
      resetScreenAfterMainTabReplace(screen);
    }
  }, [bootstrap.status, screenName, workspaceContext, workspaceTab]);

  useEffect(() => {
    const previousPathname = lastPathnameRef.current;
    const shouldRestoreHistoryBack = pendingHistoryBackRef.current;
    pendingHistoryBackRef.current = false;
    lastRouteRestorationKeyRef.current = routeRestorationKey;
    lastPathnameRef.current = pathname;
    if (bootstrap.status !== 'success') {
      return;
    }

    const screen = screenRef.current;
    if (shouldRestoreHistoryBack && screen) {
      const snapshot = routeRestorationSnapshotsRef.current.get(routeRestorationKey);
      if (snapshot) {
        restoreScreenAfterHistoryBack(screen, snapshot);
        return;
      }
    }

    if (!isWorkspaceDrillDownPath(pathname)) {
      return;
    }

    if (previousPathname === pathname && lastDrillDownResetPathRef.current === pathname) {
      return;
    }

    if (screen) {
      resetScreenAfterRouteTransition(screen);
      lastDrillDownResetPathRef.current = pathname;
    }
  }, [bootstrap.status, pathname, routeRestorationKey, screenName]);

  return (
    <ContextResetControllerContext.Provider value={contextResetController}>
      <BootstrapContext.Provider value={bootstrapContext}>
        <WorkspaceContext.Provider value={workspaceContext}>
          <RouteRestorationControllerContext.Provider value={routeRestorationController}>
            <WorkspaceRouteAdapterProvider>
              <main className="app-shell">
                <div className="mobile-app">
                  <section
                    ref={screenRef}
                    className={screenName === 'blocking-loading' ? 'screen centered' : 'screen'}
                    data-screen={screenName}
                    aria-busy={screenName === 'blocking-loading' ? 'true' : undefined}
                  >
                    {renderShellContent(bootstrap, runtime, { authRoute, onboardingRoute })}
                  </section>
                  {shouldShowBottomBar && workspaceTab ? <BottomBar activeTab={workspaceTab} /> : null}
                </div>
              </main>
            </WorkspaceRouteAdapterProvider>
          </RouteRestorationControllerContext.Provider>
        </WorkspaceContext.Provider>
      </BootstrapContext.Provider>
    </ContextResetControllerContext.Provider>
  );
}

function renderShellContent(
  bootstrap: ReturnType<typeof useBootstrapQuery>,
  runtime: AppRuntime,
  routes: AppShellProps
): ReactNode {
  if (bootstrap.status === 'pending') {
    return (
      <div className="panel">
        <h1 className="page-title" tabIndex={-1}>
          加载中
        </h1>
        <p>正在进入 Choir Nest。</p>
      </div>
    );
  }

  if (bootstrap.status === 'error') {
    const canResetLocalData =
      bootstrap.error.message === MEMBER_MANAGEMENT_DATA_RESET_REQUIRED_MESSAGE && Boolean(runtime.resetLocalMvpData);
    return (
      <BlockingErrorPanel
        error={bootstrap.error}
        canResetLocalData={canResetLocalData}
        onResetLocalData={() => {
          void (async () => {
            await runtime.resetLocalMvpData?.();
            await bootstrap.refetch();
          })();
        }}
        onRetry={() => void bootstrap.refetch()}
      />
    );
  }

  if (!bootstrap.data.currentUser) {
    return routes.authRoute;
  }

  if (!bootstrap.data.currentChoir || !bootstrap.data.currentSeason) {
    return routes.onboardingRoute;
  }

  return <Outlet />;
}

function BlockingErrorPanel({
  error,
  canResetLocalData,
  onResetLocalData,
  onRetry
}: {
  readonly error: Error;
  readonly canResetLocalData: boolean;
  readonly onResetLocalData: () => void;
  readonly onRetry: () => void;
}): ReactNode {
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    errorRef.current?.focus();
  }, [error]);

  return (
    <div className="panel error" role="alert" tabIndex={-1} ref={errorRef}>
      <h1 className="page-title" tabIndex={-1}>
        无法进入应用
      </h1>
      <p>{formatBlockingError(error)}</p>
      {canResetLocalData ? (
        <button type="button" data-action="reset-local-mvp-data" onClick={onResetLocalData}>
          重置本地数据
        </button>
      ) : null}
      <button type="button" data-action="retry-blocking-bootstrap" onClick={onRetry}>
        重试
      </button>
    </div>
  );
}

function BottomBar({ activeTab }: { readonly activeTab: WorkspaceTab }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const items: readonly {
    readonly tab: WorkspaceTab;
    readonly action: string;
    readonly label: string;
    readonly icon: IconName;
  }[] = [
    { tab: 'home', action: 'switch-tab-home', label: '首页', icon: 'home' },
    { tab: 'assignments', action: 'switch-tab-assignments', label: '作业', icon: 'music' },
    { tab: 'schedule', action: 'switch-tab-schedule', label: '日程', icon: 'calendar' },
    { tab: 'resources', action: 'switch-tab-resources', label: '资料', icon: 'folder' },
    { tab: 'members', action: 'switch-tab-members', label: '成员', icon: 'users' }
  ];

  return (
    <nav className="bottom-nav" aria-label="主导航">
      {items.map((item) => (
        <button
          key={item.tab}
          type="button"
          className={item.tab === activeTab ? 'bottom-nav-item active' : 'bottom-nav-item'}
          data-action={item.action}
          aria-current={item.tab === activeTab ? 'page' : undefined}
          onClick={() => {
            if (item.tab === activeTab) {
              return;
            }
            routeAdapter.replaceMainTab(item.tab);
          }}
        >
          <Icon name={item.icon} />
          <span className="bottom-nav-label">{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

function getAppBootstrapContext(state: MemberManagementViewModel): AppBootstrapContext {
  return {
    currentUser: state.currentUser,
    currentChoir: state.currentChoir,
    currentMembership: state.currentMembership,
    currentSeason: state.currentSeason,
    isCurrentUserAdmin: state.isCurrentUserAdmin,
    currentPermissionKeys: state.currentPermissionKeys,
    joinedChoirs: state.joinedChoirs,
    ownJoinRequests: state.ownJoinRequests,
    schedules: state.schedules,
    members: state.members
  };
}

function getWorkspaceContext(state: MemberManagementViewModel | undefined): WorkspaceContextValue | null {
  if (!state?.currentUser || !state.currentChoir || !state.currentSeason) {
    return null;
  }

  return {
    currentUserId: state.currentUser.userId,
    currentChoirId: state.currentChoir.choirId,
    currentSeasonId: state.currentSeason.seasonId
  };
}

function getScreenName(
  state: MemberManagementViewModel | undefined,
  status: 'error' | 'pending' | 'success',
  pathname: string,
  workspaceTab: WorkspaceTab | null
): string {
  if (status === 'pending') {
    return 'blocking-loading';
  }

  if (status === 'error') {
    return 'blocking-error';
  }

  if (!state?.currentUser) {
    return 'auth-entry';
  }

  if (!state.currentChoir || !state.currentSeason) {
    return 'onboarding-choice';
  }

  return getWorkspaceRouteScreenName(pathname, workspaceTab);
}

function getWorkspaceRouteScreenName(pathname: string, workspaceTab: WorkspaceTab | null): string {
  if (workspaceTab) {
    return `workspace-${workspaceTab}`;
  }

  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  if (/^\/workspace\/schedules\/new\/time$/.test(normalizedPathname)) {
    return 'schedule-time-picker';
  }
  if (/^\/workspace\/schedules\/[^/]+\/edit\/time$/.test(normalizedPathname)) {
    return 'schedule-time-picker';
  }
  if (/^\/workspace\/schedules\/new\/attendance$/.test(normalizedPathname)) {
    return 'schedule-attendance-settings';
  }
  if (/^\/workspace\/schedules\/[^/]+\/edit\/attendance$/.test(normalizedPathname)) {
    return 'schedule-attendance-settings';
  }
  if (/^\/workspace\/schedules\/new$/.test(normalizedPathname)) {
    return 'schedule-create';
  }
  if (/^\/workspace\/schedules\/[^/]+\/edit$/.test(normalizedPathname)) {
    return 'schedule-edit';
  }
  if (/^\/workspace\/schedules\/[^/]+\/delete$/.test(normalizedPathname)) {
    return 'confirm-delete-schedule';
  }
  if (/^\/workspace\/schedules\/[^/]+$/.test(normalizedPathname)) {
    return 'schedule-detail';
  }
  if (/^\/workspace\/assignments\/[^/]+\/submission$/.test(normalizedPathname)) {
    return 'assignment-upload';
  }
  if (/^\/workspace\/assignments\/new$/.test(normalizedPathname)) {
    return 'assignment-create';
  }
  if (/^\/workspace\/assignments\/[^/]+$/.test(normalizedPathname)) {
    return 'assignment-detail';
  }
  if (/^\/workspace\/resources\/songs\/[^/]+\/folders\/[^/]+$/.test(normalizedPathname)) {
    return 'song-related-folder';
  }
  if (/^\/workspace\/resources\/songs\/[^/]+$/.test(normalizedPathname)) {
    return 'song-detail';
  }
  if (/^\/workspace\/resources\/files\/[^/]+$/.test(normalizedPathname)) {
    return 'resource-preview';
  }

  switch (normalizedPathname) {
    case '/workspace/notifications':
      return 'notifications';
    case '/workspace/manage/choir':
      return 'choir-management';
    case '/workspace/settings/schedules':
      return 'schedule-settings';
    case '/workspace/settings/members':
      return 'member-settings';
    case '/workspace/settings/season':
      return 'season-settings';
    case '/workspace/manage/approvals':
      return 'approval-list';
    case '/workspace/manage/invitations':
      return 'invitation-codes';
    case '/workspace/member-groups':
      return 'manage-member-groups';
    case '/workspace/member-groups/new':
      return 'create-member-group';
    case '/workspace/member-groups/new/initial-member':
      return 'create-member-group-initial-member';
    case '/workspace/permission-groups':
      return 'permission-groups';
    case '/workspace/permission-groups/new':
      return 'create-permission-group';
    case '/workspace/settings/attendance-categories/new':
      return 'create-attendance-category';
  }

  if (/^\/workspace\/manage\/approvals\/[^/]+$/.test(normalizedPathname)) {
    return 'approval-detail';
  }
  if (/^\/workspace\/settings\/attendance-categories\/[^/]+\/rename$/.test(normalizedPathname)) {
    return 'rename-attendance-category';
  }
  if (/^\/workspace\/settings\/attendance-categories\/[^/]+\/disable$/.test(normalizedPathname)) {
    return 'confirm-disable-attendance-category';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/members\/[^/]+\/remove$/.test(normalizedPathname)) {
    return 'confirm-remove-member-group-member';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/members$/.test(normalizedPathname)) {
    return 'manage-member-group-members';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/tags\/new$/.test(normalizedPathname)) {
    return 'create-member-group-tag';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/tags\/[^/]+\/rename$/.test(normalizedPathname)) {
    return 'rename-member-group-tag';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/tags\/[^/]+\/delete$/.test(normalizedPathname)) {
    return 'confirm-delete-member-group-tag';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/tags\/[^/]+$/.test(normalizedPathname)) {
    return 'member-group-tag-detail';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/tags$/.test(normalizedPathname)) {
    return 'manage-member-group-tags';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/rename$/.test(normalizedPathname)) {
    return 'rename-member-group';
  }
  if (/^\/workspace\/member-groups\/[^/]+\/delete$/.test(normalizedPathname)) {
    return 'confirm-delete-member-group';
  }
  if (/^\/workspace\/member-groups\/[^/]+$/.test(normalizedPathname)) {
    return 'member-group-detail';
  }
  if (/^\/workspace\/permission-groups\/[^/]+\/members\/[^/]+\/remove$/.test(normalizedPathname)) {
    return 'confirm-remove-permission-group-member';
  }
  if (/^\/workspace\/permission-groups\/[^/]+\/members$/.test(normalizedPathname)) {
    return 'manage-permission-group-members';
  }
  if (/^\/workspace\/permission-groups\/[^/]+\/edit$/.test(normalizedPathname)) {
    return 'edit-permission-group';
  }
  if (/^\/workspace\/permission-groups\/[^/]+\/delete$/.test(normalizedPathname)) {
    return 'confirm-delete-permission-group';
  }
  if (/^\/workspace\/permission-groups\/[^/]+$/.test(normalizedPathname)) {
    return 'permission-group-detail';
  }

  return 'workspace-detail';
}

function getWorkspaceTabForShell(state: MemberManagementViewModel | undefined, pathname: string): WorkspaceTab | null {
  const tab = getWorkspaceTabFromPath(pathname);
  if (tab) {
    return tab;
  }
  if (pathname === '/' && state?.currentUser && state.currentChoir && state.currentSeason) {
    return 'home';
  }
  return null;
}

function getWorkspaceTabFromPath(pathname: string): WorkspaceTab | null {
  const match = /^\/workspace\/([^/]+)\/?$/.exec(pathname);
  const tab = match?.[1];
  return isWorkspaceTab(tab) ? tab : null;
}

function getBootstrapContextKey(state: MemberManagementViewModel): string {
  return JSON.stringify([
    state.currentUser?.userId ?? null,
    state.currentChoir?.choirId ?? null,
    state.currentSeason?.seasonId ?? null
  ]);
}

function getBootstrapRouteResetTarget(state: MemberManagementViewModel): BootstrapRouteResetTarget {
  if (!state.currentUser) {
    return 'auth';
  }
  if (!state.currentChoir || !state.currentSeason) {
    return 'onboarding';
  }
  return 'workspace';
}

function getContextResetTargetPath(target: BootstrapRouteResetTarget): string {
  if (target === 'auth') {
    return '/auth';
  }
  if (target === 'onboarding') {
    return '/onboarding';
  }
  return '/workspace/home';
}

function resetScreenAfterContextChange(screen: HTMLElement): void {
  screen.scrollTop = 0;
  focusPageTitle(screen);
  screen.ownerDocument.defaultView?.setTimeout(() => {
    if (!screen.isConnected) {
      return;
    }
    screen.scrollTop = 0;
    focusPageTitle(screen);
  }, 0);
}

function resetScreenAfterMainTabReplace(screen: HTMLElement): void {
  screen.scrollTop = 0;
  focusPageTitle(screen);
  screen.ownerDocument.defaultView?.setTimeout(() => {
    if (!screen.isConnected) {
      return;
    }
    screen.scrollTop = 0;
    focusPageTitle(screen);
  }, 0);
}

function resetScreenAfterRouteTransition(screen: HTMLElement): void {
  screen.scrollTop = 0;
  focusPageTitle(screen);
  screen.ownerDocument.defaultView?.setTimeout(() => {
    if (!screen.isConnected) {
      return;
    }
    screen.scrollTop = 0;
    focusPageTitle(screen);
  }, 0);
}

function captureRouteRestorationSnapshot(
  snapshots: Map<string, RouteRestorationSnapshot>,
  pathname: string,
  screen: HTMLElement
): void {
  snapshots.set(pathname, {
    scrollTop: screen.scrollTop,
    focusSelector: getRestorableFocusSelector(screen.ownerDocument.activeElement)
  });
}

function restoreScreenAfterHistoryBack(screen: HTMLElement, snapshot: RouteRestorationSnapshot): void {
  screen.scrollTop = snapshot.scrollTop;
  focusRestoredElementOrPageTitle(screen, snapshot.focusSelector);
  screen.ownerDocument.defaultView?.setTimeout(() => {
    if (!screen.isConnected) {
      return;
    }
    screen.scrollTop = snapshot.scrollTop;
    focusRestoredElementOrPageTitle(screen, snapshot.focusSelector);
  }, 0);
}

function focusRestoredElementOrPageTitle(screen: HTMLElement, focusSelector: string | null): void {
  const target = focusSelector ? screen.querySelector<HTMLElement>(focusSelector) : null;
  if (target && isProgrammaticallyFocusable(target)) {
    target.focus();
    return;
  }

  focusPageTitle(screen);
}

function focusPageTitle(screen: HTMLElement): void {
  const title = screen.querySelector<HTMLElement>('.page-title, h1');
  const target = title ?? screen;
  title?.classList.add('page-title');
  target.tabIndex = -1;
  target.focus();
}

function getRestorableFocusSelector(activeElement: Element | null): string | null {
  if (!(activeElement instanceof HTMLElement)) {
    return null;
  }

  const dataAction = activeElement.getAttribute('data-action');
  if (!dataAction) {
    return null;
  }

  const dataAttributeNames = activeElement
    .getAttributeNames()
    .filter((name) => name.startsWith('data-') && activeElement.getAttribute(name))
    .sort((left, right) => {
      if (left === 'data-action') {
        return -1;
      }
      if (right === 'data-action') {
        return 1;
      }
      return left.localeCompare(right);
    });

  return dataAttributeNames
    .map((name) => `[${name}="${escapeCssAttributeValue(activeElement.getAttribute(name) ?? '')}"]`)
    .join('');
}

function isProgrammaticallyFocusable(element: HTMLElement): boolean {
  if (!element.isConnected) {
    return false;
  }

  if (
    element instanceof HTMLButtonElement ||
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLOptionElement
  ) {
    return !element.disabled;
  }

  return typeof element.focus === 'function';
}

function escapeCssAttributeValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function isWorkspaceDrillDownPath(pathname: string): boolean {
  return (
    /^\/workspace\/assignments\/(?:new|[^/]+(?:\/submission)?)$/.test(pathname) ||
    /^\/workspace\/notifications\/?$/.test(pathname) ||
    /^\/workspace\/resources\/songs\/[^/]+(?:\/folders\/[^/]+)?\/?$/.test(pathname) ||
    /^\/workspace\/resources\/files\/[^/]+\/?$/.test(pathname) ||
    /^\/workspace\/manage\/invitations\/?$/.test(pathname) ||
    /^\/workspace\/settings\/members\/?$/.test(pathname) ||
    /^\/workspace\/member-groups(?:\/.*)?$/.test(pathname) ||
    /^\/workspace\/permission-groups(?:\/.*)?$/.test(pathname) ||
    /^\/workspace\/schedules\/(?:new(?:\/(?:time|attendance))?|[^/]+(?:\/(?:edit(?:\/(?:time|attendance))?|delete))?)$/.test(
      pathname
    )
  );
}

function isRouteCompatibleWithBootstrapTarget(pathname: string, target: BootstrapRouteResetTarget): boolean {
  if (target === 'auth') {
    return pathname === '/auth';
  }
  if (target === 'onboarding') {
    return pathname === '/onboarding';
  }
  return pathname.startsWith('/workspace/') && pathname !== '/workspace/';
}

function formatBlockingError(error: Error): string {
  if (error.message === MEMBER_MANAGEMENT_DATA_RESET_REQUIRED_MESSAGE) {
    return MEMBER_MANAGEMENT_DATA_RESET_REQUIRED_MESSAGE;
  }
  if (error.message === 'Failed to fetch' || error.message === 'Network request failed') {
    return '网络连接或服务暂时不可用，请重试。';
  }
  return '暂时无法进入应用，请重试。';
}
